import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { theme } from '../theme';
import { getTenantSlugFromHostname } from '../utils/subdomain';

const UK_POSTCODE_REGEX = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s?\d[A-Za-z]{2}$/;
// 9 digits, must start with 1 or 2 (matches DEFRA's SBI numbering scheme) — kept
// in sync with backend/routes/auth.js's server-side SBI_REGEX.
const SBI_REGEX = /^[12]\d{8}$/;

const TermsModal = ({ terms, loading, error, onClose }) => (
  <div style={termsStyles.overlay}>
    <div style={termsStyles.modal}>
      <div style={termsStyles.header}>
        <h2 style={{ margin: 0, color: theme.colors.primary }}>RiverBid Terms of Use</h2>
        <button onClick={onClose} type="button" style={termsStyles.closeBtn}>✕</button>
      </div>
      <div style={termsStyles.body}>
        {loading && <p>Loading Terms of Use...</p>}
        {error && <p style={{ color: '#c62828' }}>{error}</p>}
        {!loading && !error && (
          <>
            <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{terms?.content}</p>
            {terms?.fileUrl && (
              <p>
                <a href={`${process.env.REACT_APP_API_URL}${terms.fileUrl}`} target="_blank" rel="noreferrer" style={{ color: theme.colors.secondary, fontWeight: 'bold' }}>
                  Download full document
                </a>
              </p>
            )}
          </>
        )}
      </div>
      <div style={termsStyles.footer}>
        <button onClick={onClose} type="button" style={styles.button}>Close</button>
      </div>
    </div>
  </div>
);

const termsStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(15, 23, 42, 0.7)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' },
  modal: { backgroundColor: '#FFFFFF', width: '600px', maxWidth: '95%', maxHeight: '85vh', borderRadius: '8px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' },
  header: { padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC' },
  closeBtn: { background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#64748B' },
  body: { padding: '20px', overflowY: 'auto' },
  footer: { padding: '15px 20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', backgroundColor: '#F8FAFC' },
};

const Register = () => {
  const { user, loadUser } = useContext(AuthContext);
  const navigate = useNavigate();
  // Tenant subdomain, if any (e.g. "wessex-water" from wessex-water.riverbid.co.uk).
  // Resolved server-side against the Client collection during registration —
  // this is only ever a hint, never treated as a trusted clientId.
  const tenantSlug = getTenantSlugFromHostname();

  // 'register' or 'verify'
  const [step, setStep] = useState('register');

  // Form Data
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', confirmPassword: '', phoneNumber: '', farmType: 'Arable (Cereals)', postcode: '', sbi: ''
  });
  const [verificationCode, setVerificationCode] = useState('');

  // UI States
  const { name, email, password, confirmPassword, phoneNumber, farmType, postcode, sbi } = formData;
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Password Visibility States
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Terms of Use acceptance
  const [currentTerms, setCurrentTerms] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsError, setTermsError] = useState('');

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  useEffect(() => {
    api.get('/terms/current')
      .then(res => setCurrentTerms(res.data))
      .catch(() => setCurrentTerms(null));
  }, []);

  const openTermsModal = () => {
    setShowTermsModal(true);
    if (!currentTerms) {
      setTermsLoading(true);
      setTermsError('');
      api.get('/terms/current')
        .then(res => setCurrentTerms(res.data))
        .catch(err => setTermsError(err.response?.data?.msg || 'Failed to load Terms of Use'))
        .finally(() => setTermsLoading(false));
    }
  };

  const onChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // 🔒 PASSWORD VALIDATION LOGIC
  const validatePasswordStrength = (pwd) => {
    if (pwd.length < 8) return "Password must be at least 8 characters long.";
    
    let count = 0;
    if (/[A-Z]/.test(pwd)) count++; // Uppercase
    if (/[a-z]/.test(pwd)) count++; // Lowercase
    if (/[0-9]/.test(pwd)) count++; // Number
    if (/[^A-Za-z0-9]/.test(pwd)) count++; // Special Char
    
    if (count < 3) return "Password must contain at least 3 of: Uppercase, Lowercase, Number, or Special Character.";
    
    return null; // Valid
  };

  // STEP 1: SUBMIT REGISTRATION DETAILS
  const onSubmitRegister = async (e) => {
    e.preventDefault();
    
    // 1. Check Matching
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // 2. Check Strength
    const strengthError = validatePasswordStrength(password);
    if (strengthError) {
        setError(strengthError);
        return;
    }

    // 3. Check Farm Postcode
    if (!UK_POSTCODE_REGEX.test(postcode.trim())) {
      setError('Please enter a valid UK farm postcode');
      return;
    }

    // 4. Check SBI format, if provided (optional field)
    if (sbi.trim() && !SBI_REGEX.test(sbi.trim())) {
      setError('SBI must be 9 digits and start with 1 or 2');
      return;
    }

    // 5. Terms of Use must be explicitly accepted
    if (!termsAccepted || !currentTerms) {
      setError('Please read and accept the Terms of Use to continue');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/register', {
        name, email, password, phoneNumber, farmType, postcode: postcode.trim(),
        ...(sbi.trim() ? { sbi: sbi.trim() } : {}),
        termsVersion: currentTerms.version,
        ...(tenantSlug ? { tenantSlug } : {})
      });

      setLoading(false);
      setError('');
      setStep('verify');
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.msg || 'Registration failed');
    }
  };

// STEP 2: SUBMIT EMAIL CODE & AUTO-LOGIN
  const onSubmitVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await api.post('/auth/verify-email', { email, code: verificationCode });
      
      if (res.data.token) {
          localStorage.setItem('token', res.data.token);
          api.defaults.headers.common['x-auth-token'] = res.data.token;
          await loadUser(); 
          navigate('/dashboard');
      } else {
          navigate('/login');
      }

    } catch (err) {
      setLoading(false);
      console.error("Verification Error:", err);
      setError(err.response?.data?.msg || 'Verification failed. Please try logging in manually.');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.formBox}>
        <h2 style={{ textAlign: 'center', marginBottom: '10px', color: theme.colors.primary }}>
            {step === 'register' ? 'Create Account' : 'Verify Email'}
        </h2>

        {step === 'register' && (
            <>
                <p style={{textAlign:'center', fontSize:'0.9rem', color:'#7f8c8d', marginBottom:'10px'}}>Join the marketplace to trade land services.</p>
                <p style={{textAlign:'center', fontSize:'0.8rem', color:'#7f8c8d', marginBottom:'20px'}}>* = mandatory field</p>
            </>
        )}

        {error && <div style={styles.error}>{error}</div>}

        {step === 'register' ? (
            // --- FORM 1: DETAILS ---
            <form onSubmit={onSubmitRegister}>
                <div style={styles.inputGroup}><label style={styles.label}>Full Name *</label><input type="text" name="name" value={name} onChange={onChange} required style={styles.input} /></div>
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Email Address *</label>
                    <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={onChange}
                        required
                        style={styles.input}
                    />
                </div>
                <div style={styles.inputGroup}><label style={styles.label}>Mobile Number (optional)</label><input type="text" name="phoneNumber" value={phoneNumber} onChange={onChange} placeholder="+44 7700 900000" style={styles.input} /></div>
                <div style={styles.inputGroup}><label style={styles.label}>Farm Postcode *</label><input type="text" name="postcode" value={postcode} onChange={onChange} placeholder="e.g. SN9 5PG" required style={styles.input} /></div>
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Single Business Identifier / SBI (optional)</label>
                    <input type="text" name="sbi" value={sbi} onChange={onChange} placeholder="9-digit SBI, e.g. 123456789" maxLength={9} style={styles.input} />
                    <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '5px' }}>
                        If you know your farm's SBI, we'll load your field boundaries automatically on first login.
                    </div>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Farm Type *</label>
                  <select name="farmType" value={farmType} onChange={onChange} style={styles.input}>
                      <option value="Arable (Cereals)">Arable (Cereals)</option>
                      <option value="Arable (General Cropping)">Arable (General Cropping)</option>
                      <option value="Dairy">Dairy</option>
                      <option value="Livestock">Livestock</option>
                      <option value="Pigs">Pigs</option>
                      <option value="Poultry">Poultry</option>
                      <option value="Horticulture">Horticulture</option>
                      <option value="Mixed">Mixed</option>
                      <option value="Nature">Nature</option>
                  </select>
                </div>

                {/* PASSWORD FIELD */}
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Password *</label>
                    <div style={{fontSize: '0.75rem', color: '#666', marginBottom: '5px'}}>Min 8 chars. Must include 3 of: Uppercase, Lowercase, Number, Special.</div>
                    <div style={{position: 'relative'}}>
                        <input 
                            type={showPassword ? "text" : "password"} 
                            name="password" 
                            value={password} 
                            onChange={onChange} 
                            required 
                            style={styles.input} 
                        />
                        <button 
                            type="button" 
                            onClick={() => setShowPassword(!showPassword)} 
                            style={styles.eyeBtn}
                            title={showPassword ? "Hide Password" : "Show Password"}
                        >
                            {showPassword ? (
                                // Eye Slash (Hide)
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"/></svg>
                            ) : (
                                // Eye Open (Show)
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            )}
                        </button>
                    </div>
                </div>

                {/* CONFIRM PASSWORD FIELD */}
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Confirm Password *</label>
                    <div style={{position: 'relative'}}>
                        <input 
                            type={showConfirm ? "text" : "password"} 
                            name="confirmPassword" 
                            value={confirmPassword} 
                            onChange={onChange} 
                            required 
                            style={styles.input} 
                        />
                        <button 
                            type="button" 
                            onClick={() => setShowConfirm(!showConfirm)} 
                            style={styles.eyeBtn}
                            title={showConfirm ? "Hide Password" : "Show Password"}
                        >
                            {showConfirm ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"/></svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            )}
                        </button>
                    </div>
                </div>

                <div style={{ ...styles.inputGroup, display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <input
                        type="checkbox"
                        id="terms-accept"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        style={{ marginTop: '3px' }}
                    />
                    <label htmlFor="terms-accept" style={{ fontSize: '0.85rem', color: theme.colors.primary }}>
                        I have read and accept the{' '}
                        <button type="button" onClick={openTermsModal} style={{ background: 'none', border: 'none', padding: 0, color: theme.colors.secondary, fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>
                            RiverBid Terms of Use
                        </button>
                        {' '}(including the Privacy Policy) *
                    </label>
                </div>

                <button type="submit" style={styles.button} disabled={loading}>{loading ? 'Sending...' : 'Next'}</button>
            </form>
        ) : (
            // --- FORM 2: VERIFICATION CODE ---
            <form onSubmit={onSubmitVerify}>
                <p style={{textAlign:'center', marginBottom:'20px', color: '#555'}}>
                    We sent a code to <strong>{email}</strong>.<br/>Please enter it below.
                </p>
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Verification Code</label>
                    <input 
                        type="text" 
                        value={verificationCode} 
                        onChange={(e) => setVerificationCode(e.target.value)} 
                        placeholder="123456" 
                        style={{...styles.input, textAlign:'center', letterSpacing:'5px', fontSize:'1.2rem'}} 
                        required 
                    />
                </div>
                <button type="submit" style={styles.button} disabled={loading}>{loading ? 'Verifying...' : 'Verify & Login'}</button>
                <button type="button" onClick={() => setStep('register')} style={{...styles.button, backgroundColor:'#bdc3c7', marginTop:'10px'}}>Back</button>
            </form>
        )}

        {step === 'register' && (
            <p style={{ marginTop: '20px', textAlign: 'center', fontSize:'0.9rem' }}>
                Already have an account? <Link to="/login" style={{color: theme.colors.accent, fontWeight:'bold', textDecoration:'none'}}>Login</Link>
            </p>
        )}
      </div>

      {showTermsModal && (
        <TermsModal
          terms={currentTerms}
          loading={termsLoading}
          error={termsError}
          onClose={() => setShowTermsModal(false)}
        />
      )}
    </div>
  );
};

const styles = {
  container: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: theme.colors.background, padding: theme.spacing(2.5) },
  formBox: { width: '450px', padding: theme.spacing(5), backgroundColor: 'white', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', borderRadius: '12px' },
  inputGroup: { marginBottom: theme.spacing(1.9) },
  label: { display: 'block', marginBottom: theme.spacing(0.75), fontSize: '0.9rem', fontWeight: '600', color: theme.colors.primary },
  input: { width: '100%', padding: theme.spacing(1.5), border: '1px solid #dfe6e9', borderRadius: '6px', fontSize: '1rem', boxSizing: 'border-box', outline: 'none', transition: 'border 0.2s' },
  inputLocked: { backgroundColor: theme.colors.background, color: '#7f8c8d', cursor: 'not-allowed' },
  button: { width: '100%', padding: theme.spacing(1.75), backgroundColor: theme.colors.secondary, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', marginTop: theme.spacing(1.25) },
  error: { backgroundColor: '#ffebee', color: '#c62828', padding: theme.spacing(1.5), marginBottom: theme.spacing(2.5), borderRadius: '6px', textAlign: 'center', fontSize: '0.9rem' },
  // Updated professional icon style
  eyeBtn: { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }
};

export default Register;